import type { QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import type { Models } from "api/types";
import { hasCancellationRunwayAt, hasFutureScheduledCancellation } from "utils/membershipStatus";

import {
  getPlatformUserMemberships,
  getPlatformUserMembershipsByPlatformUserMembershipIdChangeOptions,
  getPlatformUserMembershipsByPlatformUserMembershipIdFreeze,
  getPlatformUserMembershipsStatus,
} from "api/methods";

// The maximum amount of times a subscription payment will be attempted. At the moment, there is the initial payment attempt and 3 retries.
// see: https://dashboard.stripe.com/revenue-recovery/retries
const MAX_PAYMENT_RETRIES = 4;

/** Mirrors the backend MembershipFreezeStatus enum (serialized as an integer). */
export enum MembershipFreezeStatus {
  PendingApproval = 0,
  Approved = 1,
  Active = 2,
  Completed = 3,
  Denied = 4,
  Withdrawn = 5,
  Expired = 6,
  EndedEarly = 7,
  CanceledWithMembership = 8,
}

/** True when the freeze request is waiting on provider approval. */
export const isFreezePending = (freeze?: Models<"DisplayMembershipFreeze"> | null): boolean =>
  freeze?.status === MembershipFreezeStatus.PendingApproval;

/**
 * True when the freeze has been approved but has not started yet (startsAt in the
 * future). The membership is still active and billing until startsAt; resuming at
 * this point cancels the scheduled freeze without charging the member.
 */
export const isFreezeScheduled = (
  freeze?: Models<"DisplayMembershipFreeze"> | null,
  now: Date = new Date(),
): boolean => freeze?.status === MembershipFreezeStatus.Approved && new Date(freeze.startsAt) > now;

/**
 * True when the freeze is in effect: the backend has marked it Active, or it is
 * Approved and startsAt has been reached. An Approved freeze with a future startsAt
 * is NOT frozen yet — see isFreezeScheduled.
 */
export const isMembershipFrozen = (
  freeze?: Models<"DisplayMembershipFreeze"> | null,
  now: Date = new Date(),
): boolean =>
  freeze?.status === MembershipFreezeStatus.Active ||
  (freeze?.status === MembershipFreezeStatus.Approved && new Date(freeze.startsAt) <= now);

/**
 * Extracts the backend-supplied message from a 4xx API error so it can be shown
 * to the user. Returns null for anything else (5xx, network errors, or 4xx
 * responses without a displayable message) — callers show their generic copy.
 */
export const get4xxErrorMessage = (error: unknown): string | null => {
  if (!isAxiosError(error) || !error.response) {
    return null;
  }

  const { status, data } = error.response;
  if (status < 400 || status >= 500) {
    return null;
  }

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data && typeof data === "object") {
    const { detail, title } = data as { detail?: unknown; title?: unknown };
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
    if (typeof title === "string" && title.trim()) {
      return title;
    }
  }

  return null;
};

/**
 * Mirrors `ManualPaymentRetryRejection.InProgressCode` on the backend: the `code` in the 400 body
 * the retry endpoint returns when another retry for the same membership still holds the claim.
 * Change both together.
 */
export const MANUAL_PAYMENT_RETRY_IN_PROGRESS_CODE = "manualPaymentRetryInProgress";

/**
 * True when the retry endpoint turned the press away because a payment retry for this membership
 * is already in progress. Nothing was attempted on the member's card and the other attempt may be
 * succeeding, so callers must not report it as a payment failure.
 */
export const isManualPaymentRetryInProgressError = (error: unknown): boolean => {
  if (!isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }

  const body: unknown = error.response.data;
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { code?: unknown }).code === MANUAL_PAYMENT_RETRY_IN_PROGRESS_CODE
  );
};

/**
 * Returns true when the membership is managed by an external system (e.g. Zenoti).
 * Matches backend MembershipHelper.IsExternalMembership: no Stripe subscription and
 * a linked Zenoti membership id.
 */
export const isMembershipManagedExternally = (
  paymentProcessorSubscriptionId?: string | null,
  zenotiId?: string | null,
): boolean => !paymentProcessorSubscriptionId?.trim() && !!zenotiId;

export const getDunningDays = (dunningStage: number) => {
  let addedDunningDays = 0;

  switch (dunningStage) {
    case 1:
      addedDunningDays = 7;
      break;
    case 2:
      addedDunningDays = 14;
      break;
    case 3:
      addedDunningDays = 21;
      break;
  }

  return addedDunningDays;
};

export const getRemainingPaymentAttempts = (dunningStage: number) => {
  return MAX_PAYMENT_RETRIES - dunningStage;
};

export const canUserReactivateMembership = (
  userMemberships: Models<"PlatformUserMembershipModel">[],
  membershipId: string,
  platformUserMembershipId: string,
  now: Date = new Date(),
) => {
  return !!(
    userMemberships
      .filter(
        ({ canceled, willCancelAt, enteredDunningAt, membership }) =>
          membership.id === membershipId &&
          !enteredDunningAt &&
          (canceled || hasFutureScheduledCancellation(willCancelAt, now)),
      )
      .reduce(
        (d1: Models<"PlatformUserMembershipModel">, d2: Models<"PlatformUserMembershipModel">) => {
          return d1.willCancelAt &&
            d2.willCancelAt &&
            new Date(d1.willCancelAt) > new Date(d2.willCancelAt)
            ? d1
            : d2;
        },
        {} as Models<"PlatformUserMembershipModel">,
      ).platformUserMembershipId === platformUserMembershipId
  );
};

/**
 * A cancellation webhook can mark an enrollment canceled before the local
 * period-end cancellation state is reconciled. When WillCancelAt is still
 * present, reactivation must rescind the existing Stripe cancellation instead
 * of sending the member through checkout for a second enrollment.
 */
export const shouldReactivateScheduledCancellation = (
  status?: {
    canceled?: boolean | null;
    willCancelAt?: string | null;
    enteredDunningAt?: string | null;
  },
  now: Date = new Date(),
): boolean => !!status?.canceled && hasCancellationRunwayAt(status, now);

/**
 * Invalidate every membership query a membership mutation (schedule/rescind/
 * apply a change, cancel, keep) can affect: the enrollment list, the status
 * rollup, and the change-options loader query. The change-options key carries
 * the membership id, so only a template match reaches it for every membership;
 * without it the change page would route on stale pendingChange.
 */
export const invalidateMembershipQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    predicate: (query) =>
      [
        getPlatformUserMemberships.filters(),
        getPlatformUserMembershipsStatus.filters(),
        getPlatformUserMembershipsByPlatformUserMembershipIdChangeOptions.filters(),
      ].some(({ predicate }) => predicate(query)),
  });

/** Invalidate the membership queries a freeze request, withdrawal or resume can affect. */
export const invalidateMembershipFreezeQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    predicate: (query) =>
      [
        getPlatformUserMemberships.filters(),
        getPlatformUserMembershipsStatus.filters(),
        getPlatformUserMembershipsByPlatformUserMembershipIdFreeze.filters(),
      ].some(({ predicate }) => predicate(query)),
  });

const isSamePaymentMethod = (
  current: Models<"PaymentMethodDetails"> | null | undefined,
  previous: Models<"PaymentMethodDetails"> | null | undefined,
): boolean => {
  if (!current || !previous) {
    return !current && !previous;
  }

  // Stripe attaches a fresh payment-method id every time a card is saved, even the same number
  // again, so the id is the sharpest signal; the visible card fields cover a response without it.
  if (current.paymentProcessorPaymentMethodId && previous.paymentProcessorPaymentMethodId) {
    return current.paymentProcessorPaymentMethodId === previous.paymentProcessorPaymentMethodId;
  }

  return (
    current.last4 === previous.last4 &&
    current.cardBrand === previous.cardBrand &&
    current.expirationMonth === previous.expirationMonth &&
    current.expirationYear === previous.expirationYear
  );
};

/**
 * True while `userMemberships` still reports the card `membershipBeforeSwap` had when the patient
 * came back from the embedded card form. Stripe confirms the new card in the browser and the
 * backend only learns about it from the `setup_intent.succeeded` webhook, so the list fetched on
 * the way back can predate the swap; the manage page keeps refetching while this holds.
 */
export const isAwaitingPaymentMethodSwap = (
  userMemberships: Models<"PlatformUserMembershipModel">[] | undefined,
  membershipBeforeSwap: Models<"PlatformUserMembershipModel"> | undefined,
): boolean => {
  if (!membershipBeforeSwap) {
    return false;
  }

  const current = userMemberships?.find(
    ({ platformUserMembershipId }) =>
      platformUserMembershipId === membershipBeforeSwap.platformUserMembershipId,
  );

  return (
    !!current &&
    isSamePaymentMethod(current.paymentMethodDetails, membershipBeforeSwap.paymentMethodDetails)
  );
};

export type CarouselSelection = { carouselKey: string; index: number };

/** Identifies a carousel's slide list; any membership added, removed or reordered changes it. */
export const getMembershipCarouselKey = (memberships: { platformUserMembershipId: string }[]) =>
  memberships.map(({ platformUserMembershipId }) => platformUserMembershipId).join(",");

/**
 * The carousel remounts at its first slide whenever its slide list changes (it is keyed on
 * `getMembershipCarouselKey`), so a selection made against an older list must fall back to the
 * first slide too; otherwise a refresh that drops the viewed membership leaves an index past the end.
 */
export const resolveVisibleMembershipIndex = (
  selection: CarouselSelection | undefined,
  carouselKey: string,
) => (selection && selection.carouselKey === carouselKey ? selection.index : 0);

/** `DisplayMembershipChangeOption.direction` value for a move to a cheaper plan. */
export const MEMBERSHIP_CHANGE_DIRECTION_DOWNGRADE = "Downgrade";

/** Router state the cancel page passes to the change page to open straight on a chosen plan. */
export type MembershipChangeLocationState = { preselectedMembershipId?: string };

type SaveOfferMembership = Pick<
  Models<"PlatformUserMembershipModel">,
  | "membership"
  | "activeMembershipFreezeId"
  | "pendingChange"
  | "hasChangeOptions"
  | "paymentProcessorSubscriptionId"
  | "zenotiId"
>;

/**
 * Mirrors the card's "Freeze" gating, including the pending-change block: the backend rejects
 * freezing while a switch is scheduled.
 */
export const canOfferFreeze = (membership: SaveOfferMembership) =>
  membership.membership.allowFreeze &&
  membership.membership.maxFreezeBillingPeriods >= 1 &&
  !membership.activeMembershipFreezeId &&
  !membership.pendingChange &&
  !isMembershipManagedExternally(membership.paymentProcessorSubscriptionId, membership.zenotiId);

/** Mirrors the card's "Change plan" gating. */
export const canOfferPlanChange = (membership: SaveOfferMembership) =>
  membership.hasChangeOptions &&
  !membership.pendingChange &&
  !isMembershipManagedExternally(membership.paymentProcessorSubscriptionId, membership.zenotiId);

/** Router state carried from the reason page through the save offers to the final confirm. */
export type MembershipCancellationLocationState = { membershipCancellationReasonId?: string };
